import * as z from "zod";
import {
  redoCartLineSchema,
  shopifyCartLineSchema,
  shopifyCartLineSchema_,
  shopifyPixelEventCartSchema,
} from "./events/storefront-event-primitives";
import type {
  AddedToCartEvent,
  AlertDisplayedPaymentFailedEvent,
  CheckoutAddressInfoSubmittedEvent,
  CheckoutCompletedEvent,
  CheckoutContactInfoSubmittedEvent,
  CheckoutShippingInfoSubmittedEvent,
  CheckoutStartedEvent,
  CollectionViewedEvent,
  EventIdentifier,
  PageViewedEvent,
  RemovedFromCartEvent,
  ViewedEvent,
} from "./shopify-storefront-event-payload";

export const trackingPageViewedPayloadSchema = z.object({
  trackingPageId: z.string(),
  url: z.string(),
  embedded: z.boolean(),
  orderId: z.string().optional(),
  fulfillmentId: z.string().optional(),
  trackingNumber: z.string().optional(),
});

export const trackingPageClickedPayloadSchema = z.object({
  clickType: z.string(),
  url: z.string(),
  orderId: z.string().optional(),
  trackingPageId: z.string().optional(),
  image: z.string().optional(),
  embedded: z.boolean().optional(),
  fulfillmentId: z.string().optional(),
  trackingNumber: z.string().optional(),
});

export const customEventPayloadSchema = z.object({
  eventName: z.string(),
  properties: z.record(z.string(), z.unknown()).optional(),
  value: z.number().optional(),
  valueCurrency: z.string().optional(),
  lineItems: z.array(redoCartLineSchema).optional(),
});

const intakeCheckoutSchema = z
  .object({
    token: z.string().nullish(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
    shippingAddress: z
      .object({ phone: z.string().nullish() })
      .passthrough()
      .nullish(),
  })
  .passthrough();

export const storefrontCarrierSchema = z.object({
  customer: z.any().nullish(),
  productVariant: shopifyCartLineSchema_.shape.merchandise
    .nullish()
    .catch(undefined),
  cartLine: shopifyCartLineSchema.nullish().catch(undefined),
  cart: shopifyPixelEventCartSchema.nullish().catch(undefined),
  checkout: intakeCheckoutSchema.nullish().catch(undefined),
  collection: z
    .object({ id: z.string(), title: z.string() })
    .nullish()
    .catch(undefined),
  urlWithoutParams: z.string().nullish().catch(undefined),
  urlWithParams: z.string().nullish().catch(undefined),
  pricingTestTreatmentId: z.string().nullish().catch(undefined),
  alertType: z.string().nullish().catch(undefined),
});

export type StorefrontCarrier = z.infer<typeof storefrontCarrierSchema>;

type StorefrontPayloadOf<E> = Omit<
  E,
  keyof EventIdentifier | "eventName" | "eventId" | "timestamp"
> & { customer?: EventIdentifier["customer"] };

export interface ShopifyWidgetEventPayloads {
  product_viewed: StorefrontPayloadOf<ViewedEvent>;
  product_added_to_cart: StorefrontPayloadOf<AddedToCartEvent>;
  product_removed_from_cart: StorefrontPayloadOf<RemovedFromCartEvent>;
  page_viewed: StorefrontPayloadOf<PageViewedEvent>;
  collection_viewed: StorefrontPayloadOf<CollectionViewedEvent>;
  checkout_started: StorefrontPayloadOf<CheckoutStartedEvent>;
  checkout_contact_info_submitted: StorefrontPayloadOf<CheckoutContactInfoSubmittedEvent>;
  checkout_address_info_submitted: StorefrontPayloadOf<CheckoutAddressInfoSubmittedEvent>;
  checkout_shipping_info_submitted: StorefrontPayloadOf<CheckoutShippingInfoSubmittedEvent>;
  checkout_completed: StorefrontPayloadOf<CheckoutCompletedEvent>;
  payment_failed: StorefrontPayloadOf<AlertDisplayedPaymentFailedEvent>;
}

export const widgetEventPayloads = {
  tracking_page_viewed: trackingPageViewedPayloadSchema,
  tracking_page_clicked: trackingPageClickedPayloadSchema,
  custom: customEventPayloadSchema,
} as const;

export type WidgetEventName = keyof typeof widgetEventPayloads;

export type SdkWidgetEventName =
  | WidgetEventName
  | keyof ShopifyWidgetEventPayloads;

export type WidgetEventPayload<N extends SdkWidgetEventName> =
  N extends WidgetEventName
    ? z.infer<(typeof widgetEventPayloads)[N]>
    : N extends keyof ShopifyWidgetEventPayloads
      ? ShopifyWidgetEventPayloads[N]
      : never;

export function isWidgetEventName(name: string): name is WidgetEventName {
  return Object.prototype.hasOwnProperty.call(widgetEventPayloads, name);
}
